import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { mdiAccount } from '@lumapps/lumx/icons';
import { Avatar, ColorPalette, ColorVariant, Icon, Link, LinkProps, Size } from '@lumapps/lumx/react';
import { Link as RouterLink } from '@lumapps/router';
import { DEFAULT_AVATAR } from '@lumapps/user/constants';
import { useUser } from '@lumapps/user/hooks/useUser';
import { BaseUserComponentProps } from '@lumapps/user/types';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';

import { useUserCardPopover } from '../../hooks/useUserCardPopover';
import { UserAvatar, UserAvatarProps } from '../UserAvatar';
import { UserCardPopoverWrapper, UserCardPopoverWrapperProps } from '../UserCardPopover/Wrapper';

import './index.scss';

export interface UserLinkProps
    extends Pick<LinkProps, 'isDisabled' | 'colorVariant' | 'target' | 'typography'>,
        BaseUserComponentProps,
        Omit<
            UserCardPopoverWrapperProps,
            | 'user'
            | 'isLoading'
            | 'showUserCard'
            | 'anchorRef'
            | 'onClick'
            | 'onMouseLeave'
            | 'onMouseEnter'
            | 'asDialog'
        > {
    /** Displays a mdi-account icon on the left of the link. */
    showIcon?: boolean;
    /** override displayed children for the link (overrides the full name specifically) */
    children?: any;
    /** whether the user avatar should be displayed or not */
    shouldDisplayAvatar?: boolean;
    /** whether the user link is displayed inline or not */
    isInline?: boolean;
    /** Link and popover trigger color */
    color?: ColorPalette;
    /** additional link props */
    linkProps?: Omit<Partial<LinkProps>, 'isDisabled' | 'color' | 'colorVariant' | 'target' | 'typography'>;
    /** additional avatar props */
    avatarProps?: Partial<UserAvatarProps>;
}

const CLASSNAME = 'user-link';

/**
 * Component to be used for displaying a link towards a given user. This component defers from UserBlock
 * since it not a block element, hence it does not display the additional information fields about the user.
 * This component can also be displayed inline.
 *
 * @param {UserLinkProps} props - The props of the component
 * @returns UserLink
 */
export const UserLink = React.forwardRef<HTMLAnchorElement, UserLinkProps>((props, ref) => {
    const {
        className,
        user,
        showIcon,
        showUserCardOnHover = true,
        isInline = false,
        children,
        isDisabled,
        color = ColorPalette.primary,
        colorVariant = ColorVariant.N,
        target,
        typography,
        shouldDisplayAvatar = false,
        avatarProps,
        scope = 'user-link',
        linkProps: additionalLinkProps,
        fallbackAvatar = DEFAULT_AVATAR,
        userCardOnHoverDelay,
        renderUserFullName,
        ...forwardedProps
    } = props;
    const { element, block } = useClassnames(CLASSNAME);
    const { userProfile: userProfileRoute, getUserFullName } = useUser(user, {
        ...forwardedProps,
        routeParams: { ...forwardedProps.routeParams, query: { details: true } },
    });

    const linkProps = !isDisabled ? { linkAs: RouterLink, to: userProfileRoute.route } : undefined;

    const {
        onMouseEnter,
        onMouseLeave,
        user: userProfile,
        isLoading,
        showUserCard,
        asDialog,
        onClick: onUserCardTriggerClick,
        anchorRef,
    } = useUserCardPopover(user, {
        shouldUseTrigger: showUserCardOnHover,
        enabled: showUserCardOnHover,
        targetClassname: ['lumx-link__content'],
        delay: userCardOnHoverDelay,
        ...forwardedProps,
    });

    return (
        <UserCardPopoverWrapper
            showUserCardOnHover={showUserCardOnHover}
            anchorRef={anchorRef}
            showUserCard={showUserCard}
            onClick={onUserCardTriggerClick}
            onMouseLeave={onMouseLeave}
            onMouseEnter={onMouseEnter}
            user={userProfile}
            isLoading={isLoading}
            asDialog={asDialog}
            isInline={isInline}
            {...forwardedProps}
            wrapperProps={{
                ...forwardedProps?.wrapperProps,
                className: element('wrapper', [forwardedProps?.wrapperProps?.className]),
            }}
            triggerProps={{
                ...forwardedProps?.triggerProps,
                color,
            }}
            userBlockProps={{
                ...forwardedProps.userBlockProps,
                avatarProps: {
                    thumbnailProps: {
                        ...forwardedProps?.userBlockProps?.avatarProps?.thumbnailProps,
                        // eslint-disable-next-line deprecation/deprecation
                        fallback: <Avatar size={Size.l} image={fallbackAvatar} alt="" />,
                    },
                },
            }}
        >
            <Link
                ref={!asDialog ? mergeRefs([ref, anchorRef as React.RefObject<HTMLAnchorElement>]) : undefined}
                {...additionalLinkProps}
                color={color}
                colorVariant={colorVariant}
                target={target}
                typography={typography}
                isDisabled={isDisabled}
                className={block([className, additionalLinkProps?.className])}
                {...linkProps}
                onMouseEnter={!isDisabled && showUserCardOnHover ? onMouseEnter : undefined}
                onMouseLeave={onMouseLeave}
            >
                {showIcon ? <Icon icon={mdiAccount} /> : null}
                {shouldDisplayAvatar ? (
                    <UserAvatar
                        scope={scope}
                        user={user}
                        size={Size.xs}
                        fallbackAvatar={fallbackAvatar}
                        {...avatarProps}
                    />
                ) : null}
                {children || renderUserFullName?.(getUserFullName()) || getUserFullName()}
            </Link>
        </UserCardPopoverWrapper>
    );
});
UserLink.displayName = 'UserLink';
